import { FilterExpression, FilterGroup, FilterGroupLeaf, UnifiedFilters } from 'types/filters';
import type { SchemaProperty } from 'types/schema';
import { evaluateFilterExpression } from './lib/filterEvaluation';
import { isPropertyExpression, isGroupFullyEvaluable } from './lib/checkApplicability';
import { isFilterVariable } from './lib/filterVariables';
import { isPropertyLeaf, isLaneBackedPropertyName } from './lib/filterGroupLeaf';
import { evaluateArchetypeFilter, evaluateArchetypePrimaryFilter } from './lib/evaluateArchetypeFilter';

/**
 * Property keys that are a UI presentation of a separate UnifiedFilters lane,
 * NOT a real sphere property. `property: 'arc'` carries an `ArcFilter` object as
 * its value (authored via the arc value editor) and belongs in the `arcFilters`
 * lane. Evaluating it as an ordinary property resolves `sphere.properties['arc']`
 * (undefined) against the ArcFilter object — which for `not_equals` yields `true`
 * and silently WIDENS applicability to universal (a never-widen hole). The
 * design-system serialization hoists these into the lane, but the server
 * validator must ALSO fail closed at the authority: a raw API caller can post
 * `{ propertyFilters:[{ property:'arc', … }] }` (or the same inside a
 * `filterGroups` leaf) directly, bypassing the UI entirely.
 */
function isLaneBackedProperty(expr: FilterExpression): boolean {
  return isLaneBackedPropertyName(expr.property);
}

function groupHasLaneBackedProperty(group: FilterGroup): boolean {
  if (group.expressions?.some(leaf => isPropertyLeaf(leaf) && isLaneBackedProperty(leaf))) return true;
  if (group.groups?.some(groupHasLaneBackedProperty)) return true;
  return false;
}

/**
 * Minimal view of a sphere for applicability evaluation.
 *
 * `properties` carries only what the CALLER chose to hydrate — it is not
 * automatically the schema-defined property bag. No caller supplies custom
 * schema properties today; see `ApplicabilityContext.sphere` for the fields
 * that are actually addressable in practice.
 */
export interface ApplicabilitySphereContext {
  id?: string;
  label?: string;
  properties: Record<string, unknown>;
  /**
   * The sphere's assigned Archetype set / primary, for the `archetype` /
   * `archetype.primary` pseudo-properties (specs/sphere-archetypes.md). A
   * caller that doesn't hydrate these leaves them `undefined` — an
   * `archetype` expression against a bag without them evaluates `false`
   * (fail closed), same as any other unresolvable reference here.
   */
  archetypes?: string[];
  primaryArchetype?: string | null;
}

export interface ApplicabilityContext {
  /**
   * The target sphere. Filters are evaluated against whatever the caller put
   * in the bag — `property: 'type'` resolves to `sphere.properties.type`,
   * `property: 'label'` resolves to the vertex label, and so on.
   *
   * The evaluator itself is generic (any key present in the bag is
   * queryable), but the hydration is NOT. Every current caller supplies a
   * narrow, fixed set of fields:
   *
   * - Arc-template deploy, metaspace add/list, approach deploy: synthesize a
   *   type-only literal `{ type: <schemaSystemName> }`. Nothing else matches.
   * - Orbit activation and metaspace resolution: hydrate via graph-service
   *   `getBasicSphereInfo` (`/spheres/:id/info`), whose valueMap is a fixed
   *   14-field list (name, description, type, sphereId, created/updated
   *   audit fields, imageId, is_private, isDraft, healthState*). The HTTP
   *   response schema strips that to 12 for out-of-process callers.
   *
   * So a filter naming a custom schema property evaluates against
   * `undefined` and fails closed — the artifact silently never matches.
   * Applicability is effectively scoped to `type`/`label` until a caller
   * hydrates a wider bag (which for `getBasicSphereInfo` callers means
   * raising the permission bar to `getSphereById`, a deliberate non-goal —
   * `/spheres/:id/info` is discover-level).
   *
   * There is no reserved or synthetic property name; to filter on
   * `schemaSystemName`, use `property: 'type'` (the actual sphere field
   * carrying the schema's system_name).
   */
  sphere: ApplicabilitySphereContext;
}

export interface ApplicabilityResult {
  valid: boolean;
  reason?: string;
}

/**
 * `@`-variable tokens (`@me`, `@root`, etc.) resolve against an execution
 * context (the current user, the context root sphere, …) that this
 * synchronous validator does not have. An applicability filter carrying one
 * would otherwise be evaluated against the literal string `'@me'` and
 * silently never match — or worse, match by accident once user data happens
 * to equal the literal token. Fail closed instead.
 */
function expressionHasFilterVariable(expr: FilterExpression): boolean {
  if (isFilterVariable(expr.value)) {
    return true;
  }
  return Array.isArray(expr.values) && expr.values.some(isFilterVariable);
}

function groupHasFilterVariable(group: FilterGroup): boolean {
  if (group.expressions?.some(leaf => isPropertyLeaf(leaf) && expressionHasFilterVariable(leaf))) {
    return true;
  }
  return Boolean(group.groups?.some(groupHasFilterVariable));
}

/**
 * `archetype` / `archetype.primary` are pseudo-properties, evaluated exactly
 * like `label`/`parentId` in `filterEngine.ts` — never resolved against
 * `context.sphere.properties` (they are not stored there), always against the
 * dedicated `archetypes`/`primaryArchetype` bag fields via the shared
 * `evaluateArchetypeFilter`/`evaluateArchetypePrimaryFilter` (single source of
 * truth with the graph-service evaluator). Everything else delegates to the
 * generic engine unchanged.
 */
function evaluateArchetypeAwareExpression(
  expr: FilterExpression,
  evalContext: Record<string, unknown>,
  schemaProperties: SchemaProperty[],
  sphere: ApplicabilitySphereContext
): boolean {
  if (expr.property === 'archetype') {
    return evaluateArchetypeFilter(sphere.archetypes, expr.operator, { value: expr.value, values: expr.values });
  }
  if (expr.property === 'archetype.primary') {
    return evaluateArchetypePrimaryFilter(sphere.primaryArchetype ?? null, expr.operator, {
      value: expr.value,
      values: expr.values,
    });
  }
  return evaluateFilterExpression(expr, evalContext, schemaProperties);
}

/**
 * Mirrors `evaluateFilterGroup`'s and/or recursion, substituting
 * `evaluateArchetypeAwareExpression` for each property leaf so an `archetype`
 * reference nested inside a `filterGroups` entry evaluates correctly too, not
 * just at the top-level `propertyFilters` lane. Lane leaves never reach here —
 * `isGroupFullyEvaluable` (called by every caller) already rejects a group
 * containing one.
 */
function evaluateArchetypeAwareGroup(
  group: FilterGroup,
  evalContext: Record<string, unknown>,
  schemaProperties: SchemaProperty[],
  sphere: ApplicabilitySphereContext
): boolean {
  const expressionResults = (group.expressions ?? []).map((leaf) =>
    isPropertyLeaf(leaf)
      ? evaluateArchetypeAwareExpression(leaf, evalContext, schemaProperties, sphere)
      : false
  );
  const groupResults = (group.groups ?? []).map((subGroup) =>
    evaluateArchetypeAwareGroup(subGroup, evalContext, schemaProperties, sphere)
  );
  const allResults = [...expressionResults, ...groupResults];
  if (allResults.length === 0) return true;
  return group.operator === 'and' ? allResults.every(Boolean) : allResults.some(Boolean);
}

/**
 * Server-side applicability validator.
 *
 * Evaluates whether an applicability `UnifiedFilters` expression matches
 * the target sphere. Filters reference real sphere properties — see
 * `ApplicabilityContext.sphere` above for which fields are addressable.
 *
 * Fail-closed (never-widen): null/undefined/malformed input (including a lane
 * present with a non-array shape), missing sphere
 * context, evaluator errors, `perspectiveFilters`, `relationshipFilters`,
 * and `arcFilters` (all three require graph traversal this synchronous
 * validator can't do) all return `{ valid: false }`. A `propertyFilters`
 * entry whose `type` is not `'property'` (e.g. `children_count`,
 * `arc_status`) is unevaluable here and also fails closed, as does a
 * `filterGroups` entry containing any such non-property expression, and any
 * expression whose `value`/`values` carries an unresolved `@`-variable
 * token (this validator has no execution context to resolve `@me`/`@root`/
 * etc. against).
 *
 * Empty applicability `{}` (no filters) is universal — valid for all spheres.
 *
 * Used by: arc-template deployment, approach deployment, metaspace manual
 * add / resolution / picker, and any other surface that needs to ask
 * "does this artifact apply to this sphere?" via UnifiedFilters.
 */
export function validateApplicability(
  applicability: UnifiedFilters | null | undefined,
  context: ApplicabilityContext,
  schemaProperties: SchemaProperty[] = []
): ApplicabilityResult {
  // Fail-closed on null/undefined
  if (applicability === null || applicability === undefined) {
    return { valid: false, reason: 'Applicability filter is null or undefined' };
  }

  // Fail-closed on malformed (not a plain object)
  if (typeof applicability !== 'object' || Array.isArray(applicability)) {
    return { valid: false, reason: 'Applicability filter is malformed (not an object)' };
  }

  const { propertyFilters, perspectiveFilters, relationshipFilters, arcFilters, filterGroups } = applicability;

  // Fail-closed on a lane present with a non-array shape. The emptiness checks
  // below read `.length`, which is `undefined` on a non-array — so a malformed
  // lane would read as absent, and a filtered applicability would fall through
  // to the universal short-circuit. That is the same fail-open the arcFilters
  // lane had, reachable via a different shape, so it is guarded before the
  // emptiness checks rather than inside them.
  const lanes: [string, unknown][] = [
    ['propertyFilters', propertyFilters],
    ['perspectiveFilters', perspectiveFilters],
    ['relationshipFilters', relationshipFilters],
    ['arcFilters', arcFilters],
    ['filterGroups', filterGroups],
  ];
  for (const [laneName, laneValue] of lanes) {
    if (laneValue !== undefined && laneValue !== null && !Array.isArray(laneValue)) {
      return {
        valid: false,
        reason: `Applicability lane '${laneName}' is malformed (expected an array)`,
      };
    }
  }

  const hasPropertyFilters = propertyFilters && propertyFilters.length > 0;
  const hasPerspectiveFilters = perspectiveFilters && perspectiveFilters.length > 0;
  const hasRelationshipFilters = relationshipFilters && relationshipFilters.length > 0;
  const hasArcFilters = arcFilters && arcFilters.length > 0;
  const hasFilterGroups = filterGroups && filterGroups.length > 0;

  // Empty applicability = universal (valid for everything)
  if (!hasPropertyFilters && !hasPerspectiveFilters && !hasRelationshipFilters && !hasArcFilters && !hasFilterGroups) {
    return { valid: true };
  }

  // Sphere context required for any non-empty filter
  if (!context?.sphere) {
    return { valid: false, reason: 'Applicability context is missing sphere' };
  }

  // Build evalContext from the sphere. Flatten properties first; overlay
  // top-level `label` and `id` so they always come from the sphere root and
  // are not shadowed by stray same-named entries in `properties`.
  const evalContext: Record<string, unknown> = {
    ...(context.sphere.properties ?? {}),
    id: context.sphere.id,
    label: context.sphere.label,
  };

  // Evaluate property filters (AND logic — all must pass)
  if (hasPropertyFilters) {
    for (const expr of propertyFilters) {
      // Non-property FilterExpressionType (children_count, arc_status, …)
      // is unevaluable synchronously. evaluateFilterExpression ignores
      // `.type` and would property-compare it anyway (e.g. resolving
      // `undefined !== 0` to `true`), so this must be checked before
      // evaluation, not left to fall through.
      if (!isPropertyExpression(expr)) {
        return {
          valid: false,
          reason: `Applicability contains a non-property filter expression (property '${expr.property}', type '${expr.type}') which cannot be evaluated synchronously`,
        };
      }

      // Lane-backed property key (e.g. `arc`) is not a real sphere property —
      // it carries a lane object as its value and would resolve `undefined`
      // against it, widening applicability. Fail closed; it belongs in its lane.
      if (isLaneBackedProperty(expr)) {
        return {
          valid: false,
          reason: `Applicability contains a lane-backed filter ('${expr.property}') in propertyFilters, which is not evaluable as a sphere property and must use its dedicated lane`,
        };
      }

      if (expressionHasFilterVariable(expr)) {
        return {
          valid: false,
          reason: `Applicability filter for '${expr.property}' contains an unresolved '@' variable, which is not supported in applicability context`,
        };
      }


      try {
        const result = evaluateArchetypeAwareExpression(expr, evalContext, schemaProperties, context.sphere);
        if (!result) {
          return { valid: false, reason: `Applicability does not match: '${expr.property}' filter failed` };
        }
      } catch {
        return { valid: false, reason: `Applicability evaluation failed (property '${expr.property}' filter error)` };
      }
    }
  }

  // Evaluate filter groups (AND across groups; each group's internal logic is its own and/or)
  if (hasFilterGroups) {
    for (const group of filterGroups) {
      // Mirror the propertyFilters guard: a group containing any
      // non-property expression (recursively, incl. nested groups) is
      // unevaluable synchronously and must fail closed rather than being
      // silently property-compared by evaluateFilterGroup.
      if (!isGroupFullyEvaluable(group)) {
        return {
          valid: false,
          reason: 'Applicability filter group contains a non-property expression which cannot be evaluated synchronously',
        };
      }

      // Same lane-backed guard as the property loop, but recursive across
      // nested groups — a `{property:'arc'}` leaf inside a group would otherwise
      // resolve `undefined !== value` → true → widen the group to universal.
      if (groupHasLaneBackedProperty(group)) {
        return {
          valid: false,
          reason: "Applicability filter group contains a lane-backed filter ('arc') which is not evaluable as a sphere property and must use its dedicated lane",
        };
      }

      if (groupHasFilterVariable(group)) {
        return {
          valid: false,
          reason: "Applicability filter group contains an unresolved '@' variable, which is not supported in applicability context",
        };
      }

      try {
        const result = evaluateArchetypeAwareGroup(group, evalContext, schemaProperties, context.sphere);
        if (!result) {
          return { valid: false, reason: 'Applicability filter group did not match' };
        }
      } catch {
        return { valid: false, reason: 'Applicability evaluation failed (filter group error)' };
      }
    }
  }

  // perspectiveFilters require graph traversal — not supported by this
  // synchronous validator. Treat as fail-closed if present.
  if (hasPerspectiveFilters) {
    return {
      valid: false,
      reason: 'Applicability contains perspectiveFilters which require graph traversal and are not supported in this context',
    };
  }

  // relationshipFilters likewise require graph traversal (relationship edges
  // are not part of the sphere property bag) — fail-closed if present, never
  // silently ignore a lane (never-widen).
  if (hasRelationshipFilters) {
    return {
      valid: false,
      reason: 'Applicability contains relationshipFilters which require graph traversal and are not supported in this context',
    };
  }

  // arcFilters require live graph traversal of the sphere's arcs (stage,
  // health, lifecycle) — not part of the sphere property bag this validator
  // evaluates against. Previously unhandled, which meant `{ arcFilters: […] }`
  // fell through every hasXFilters check and was treated as empty (universal)
  // — a fail-open hole. Fail closed if present, never silently ignore a lane.
  if (hasArcFilters) {
    return {
      valid: false,
      reason: 'Applicability contains arcFilters which require graph traversal and are not supported in this context',
    };
  }

  return { valid: true };
}

/**
 * Authoring-side guard: detects whether a `UnifiedFilters` applicability
 * expression contains a `property: 'organizationId'` clause, searching
 * `propertyFilters` and (recursively) `filterGroups`.
 *
 * Applicability describes spheres (type/label/properties), not tenants —
 * cross-org access is granted via the SpiceDB `deploy` permission (channel
 * subscription), not by an org-id clause in applicability. Authoring
 * surfaces (create/update template, context, metaspace) call this to reject
 * the clause outright. See `/standards/shared/filtering.md` Rule 9.
 */
export function applicabilityHasOrganizationIdClause(
  applicability: UnifiedFilters | null | undefined
): boolean {
  if (!applicability || typeof applicability !== 'object' || Array.isArray(applicability)) {
    return false;
  }

  const expressionsReferenceOrgId = (
    expressions?: ReadonlyArray<FilterExpression | FilterGroupLeaf>
  ): boolean =>
    !!expressions?.some((expr) => 'property' in expr && expr.property === 'organizationId');

  const groupsReferenceOrgId = (groups?: FilterGroup[]): boolean =>
    !!groups?.some(
      (group) => expressionsReferenceOrgId(group.expressions) || groupsReferenceOrgId(group.groups)
    );

  return (
    expressionsReferenceOrgId(applicability.propertyFilters) ||
    groupsReferenceOrgId(applicability.filterGroups)
  );
}

/**
 * Authoring-side guard: detects whether a `UnifiedFilters` applicability
 * expression contains a `property: 'schemaSystemName'` clause, searching
 * `propertyFilters` and (recursively) `filterGroups`.
 *
 * `schemaSystemName` was a synthetic field the old (pre-UnifiedFilters)
 * applicability evaluator hardcoded. The current `validateApplicability`
 * evaluates only real sphere properties — `property: 'type'` is the actual
 * field carrying the schema's system_name — so a filter authored against
 * `schemaSystemName` silently never matches at eval time (see the
 * regression test in applicabilityValidator.spec.ts pinning that behavior).
 * This guard catches the mistake at authoring time instead of letting a
 * template ship that can never attach anywhere. See
 * `/standards/shared/filtering.md` Rule 9.
 */
export function applicabilityHasSchemaSystemNameClause(
  applicability: UnifiedFilters | null | undefined
): boolean {
  if (!applicability || typeof applicability !== 'object' || Array.isArray(applicability)) {
    return false;
  }

  const expressionsReferenceSchemaSystemName = (
    expressions?: ReadonlyArray<FilterExpression | FilterGroupLeaf>
  ): boolean =>
    !!expressions?.some((expr) => 'property' in expr && expr.property === 'schemaSystemName');

  const groupsReferenceSchemaSystemName = (groups?: FilterGroup[]): boolean =>
    !!groups?.some(
      (group) =>
        expressionsReferenceSchemaSystemName(group.expressions) ||
        groupsReferenceSchemaSystemName(group.groups)
    );

  return (
    expressionsReferenceSchemaSystemName(applicability.propertyFilters) ||
    groupsReferenceSchemaSystemName(applicability.filterGroups)
  );
}
